/**
 * Commissions Due Page
 *
 * Shows overdue shipments where commission payments have not been received.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Button,
  Select,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  WarningOutlined,
  ReloadOutlined,
  DownloadOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface Commission {
  id: string;
  contractRef: string;
  supplier: string;
  buyer: string;
  commodity: string;
  commissionAmount: number;
  commissionRate: number;
  totalValue: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  dueDate: string;
  paidDate: string | null;
  contractDate: string;
}

interface Company {
  id: string;
  name: string;
  shortName?: string;
  type: string;
}

const CommissionsDue: React.FC = () => {
  const navigate = useNavigate();
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [buyerId, setBuyerId] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<'OVERDUE' | 'PENDING' | 'ALL'>('OVERDUE');

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'all'],
    queryFn: async () => {
      const response = await api.get('/companies', { params: { limit: 500 } });
      return (response as any).companies || [];
    },
  });

  const companies: Company[] = companiesData || [];
  const suppliers = companies.filter((c) => c.type === 'SUPPLIER');
  const buyers = companies.filter((c) => c.type === 'BUYER');

  const { data: commissionsData, isLoading, refetch } = useQuery({
    queryKey: ['commissions', statusFilter, supplierId, buyerId],
    queryFn: async () => {
      const params: any = { 
        status: statusFilter,
        limit: 200,
      };
      if (supplierId) params.supplierId = supplierId;
      if (buyerId) params.buyerId = buyerId;
      
      const response = await api.get('/financial/commissions', { params });
      const data = (response as any)?.data || response;
      return data as { commissions: Commission[]; pagination: any };
    },
  });

  const commissions = commissionsData?.commissions || [];

  const totals = commissions.reduce(
    (acc, c) => {
      acc.totalAmount += c.commissionAmount;
      if (c.status === 'OVERDUE') {
        acc.overdueAmount += c.commissionAmount;
        acc.overdueCount++;
      }
      return acc;
    },
    { totalAmount: 0, overdueAmount: 0, overdueCount: 0 }
  );

  const handleExport = async () => {
    try {
      const params: any = { reportType: 'overdue-shipments' };
      if (supplierId) params.supplierId = supplierId;
      if (buyerId) params.buyerId = buyerId;

      const response = await api.get('/export', { 
        params,
        responseType: 'blob' as any,
      });
      
      const blob = new Blob([response as any], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `commissions-due-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('Export downloaded successfully');
    } catch (error) {
      message.error('Failed to export report');
    }
  };

  const handleRowClick = (record: Commission) => {
    navigate(`/contracts/${record.contractRef}`);
  };

  const getDaysOverdue = (dueDate: string): number => {
    const due = dayjs(dueDate);
    const today = dayjs();
    return today.diff(due, 'day');
  };

  const columns: ColumnsType<Commission> = [
    {
      title: 'Contract',
      dataIndex: 'contractRef',
      key: 'contractRef',
      width: 120,
      render: (ref: string) => <Text strong>{ref}</Text>,
      sorter: (a, b) => a.contractRef.localeCompare(b.contractRef),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Contract Date',
      dataIndex: 'contractDate',
      key: 'contractDate',
      width: 110,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.contractDate).unix() - dayjs(b.contractDate).unix(),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
    },
    {
      title: 'Days Overdue',
      key: 'daysOverdue',
      width: 110,
      render: (_, record) => {
        const days = getDaysOverdue(record.dueDate);
        if (days <= 0) return <Text type="secondary">Not due</Text>;
        return (
          <Tag color={days > 30 ? 'red' : days > 14 ? 'orange' : 'gold'} icon={<WarningOutlined />}>
            {days} days
          </Tag>
        );
      },
      sorter: (a, b) => getDaysOverdue(a.dueDate) - getDaysOverdue(b.dueDate),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Rate',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 80,
      align: 'right',
      render: (rate: number) => `${rate}%`,
    },
    {
      title: 'Amount Due',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      width: 120,
      align: 'right',
      render: (amount: number) => (
        <Text strong style={{ color: colors.status.negative }}>
          ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </Text>
      ),
      sorter: (a, b) => a.commissionAmount - b.commissionAmount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusColors: Record<string, string> = {
          PENDING: 'gold',
          OVERDUE: 'red',
          PAID: 'green',
        };
        return <Tag color={statusColors[status] || 'default'}>{status}</Tag>;
      },
    },
  ];

  return (
    <div style={{ padding: spacing.xl }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>Commissions Due</Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export Excel</Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Overdue"
                value={totals.overdueAmount}
                precision={2}
                prefix="$"
                valueStyle={{ color: colors.status.negative }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Overdue Contracts"
                value={totals.overdueCount}
                prefix={<WarningOutlined style={{ color: colors.status.negative }} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Pending"
                value={totals.totalAmount}
                precision={2}
                prefix="$"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Contracts Shown"
                value={commissions.length}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space style={{ marginBottom: 16 }} wrap>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140 }}
              options={[
                { value: 'OVERDUE', label: 'Overdue Only' },
                { value: 'PENDING', label: 'Pending Only' },
                { value: 'ALL', label: 'All Status' },
              ]}
            />
            <Select
              value={supplierId}
              onChange={setSupplierId}
              style={{ width: 180 }}
              placeholder="Filter by Supplier"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={suppliers.map((s) => ({
                value: s.id,
                label: s.shortName || s.name,
              }))}
            />
            <Select
              value={buyerId}
              onChange={setBuyerId}
              style={{ width: 180 }}
              placeholder="Filter by Buyer"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={buyers.map((b) => ({
                value: b.id,
                label: b.shortName || b.name,
              }))}
            />
            {(supplierId || buyerId || statusFilter !== 'OVERDUE') && (
              <Button
                onClick={() => {
                  setSupplierId(undefined);
                  setBuyerId(undefined);
                  setStatusFilter('OVERDUE');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={commissions}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total) => `${total} contracts`,
            }}
            size="middle"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      </Space>
    </div>
  );
};

export default CommissionsDue;
